import { FeetInchUnit, LabelFormat, LabelSymbol } from '@trimble/types';
import { LengthUnit } from '@fulcrum/lib';
import { convertLength } from './length-converter';

/**
 * Parses a label format template to extract its structural information.
 *
 * A format template like `"1.0+00.0"` is interpreted as follows:
 * - Everything after the **last** `"."` is the decimal part (`"0"` → 1 decimal digit).
 * - Everything before it is the integer template, which may contain:
 *   - A symbol character (`+` or `/`),
 *   - Extra `"."` chars that become thousands-separator dots in the output.
 *
 * The minimum number of integer digits is determined by counting all `"0"` and `"1"`
 * characters in the integer template.
 */
interface ParsedFormat {
  /** Minimum number of integer digits to output. */
  minIntegerDigits: number;
  /** Number of decimal places to output. */
  decimalPlaces: number;
  /**
   * Describes the characters in the integer part of the template, from left to right.
   * Each entry is either:
   *   - `{ type: 'digit' }` — a digit placeholder,
   *   - `{ type: 'separator', char: '.' | '+' | '/' }` — a literal separator to insert.
   */
  integerParts: Array<{ type: 'digit' } | { type: 'separator'; char: string }>;
}

function parseFormat(format: LabelFormat): ParsedFormat {
  // Find the last dot — everything after it is decimals
  const lastDotIndex = format.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // No decimal part
    return parseIntegerTemplate(format, 0);
  }

  const integerTemplate = format.substring(0, lastDotIndex);
  const decimalTemplate = format.substring(lastDotIndex + 1);
  const decimalPlaces = decimalTemplate.length;

  return parseIntegerTemplate(integerTemplate, decimalPlaces);
}

function parseIntegerTemplate(
  template: string,
  decimalPlaces: number
): ParsedFormat {
  const integerParts: ParsedFormat['integerParts'] = [];
  let minIntegerDigits = 0;

  for (const ch of template) {
    if (ch === '0' || ch === '1') {
      integerParts.push({ type: 'digit' });
      minIntegerDigits++;
    } else {
      // Any non-digit char ('+', '/', '.') is a literal separator
      integerParts.push({ type: 'separator', char: ch });
    }
  }

  return { minIntegerDigits, decimalPlaces, integerParts };
}

/**
 * Formats a numeric chainage value (already in the display unit) according to
 * the given label format template.  No unit conversion is performed.
 *
 * @param displayValue The chainage value already expressed in the display unit.
 * @param format The label format template (e.g. `"1.0+00.0"`, `"10/00.0"`).
 * @param lengthDecimals Overrides the number of decimal places in the template if provided.
 *                       When undefined, the template's own decimal places are used.
 * @returns The formatted chainage string.
 *
 * @example
 * ```ts
 * formatChainageDisplayValue(150,    '10+00.0')    // "01+50.0"
 * formatChainageDisplayValue(1234.5, '1.0+00.0')   // "1.2+34.5"
 * formatChainageDisplayValue(1234.5, '1000.0')      // "1234.5"
 * formatChainageDisplayValue(150,    '1+000.0')     // "0+150.0"
 * ```
 */
export function formatChainageDisplayValue(
  displayValue: number,
  format: LabelFormat,
  lengthDecimals?: number
): string {
  const parsed = parseFormat(format);
  const decimals = lengthDecimals ?? parsed.decimalPlaces;

  const isNegative = displayValue < 0;
  const absValue = Math.abs(displayValue);

  // Round the full value via toFixed and split the *string* so that
  // decimal-to-integer carry is never lost (e.g. 9.96 with 1 decimal
  // → "10.0", not integer 9 + decimal "1.0").
  const roundedStr = absValue.toFixed(decimals);

  let integerPart: number;
  let decimalStr: string;

  if (decimals > 0) {
    const dotIdx = roundedStr.indexOf('.');
    integerPart = parseInt(roundedStr.substring(0, dotIdx), 10);
    decimalStr = roundedStr.substring(dotIdx); // includes the "."
  } else {
    integerPart = parseInt(roundedStr, 10);
    decimalStr = '';
  }

  // Pad the integer to the minimum number of digits
  const integerStr = integerPart
    .toString()
    .padStart(parsed.minIntegerDigits, '0');

  // Now we insert the separator characters at the correct positions.
  // The integer template defines positions from left to right, where each
  // `digit` placeholder maps to the integer digits from left to right.
  // If the actual integer has more digits than the minimum, extra digits
  // appear at the leftmost position.

  // Count how many digit slots the template has
  const templateDigitCount = parsed.integerParts.filter(
    (p) => p.type === 'digit'
  ).length;
  const actualDigitCount = integerStr.length;

  // If more digits than template slots, we need extra leading digits
  const extraDigits = Math.max(0, actualDigitCount - templateDigitCount);

  let result = '';
  let digitIndex = 0;

  // First, emit any extra leading digits
  if (extraDigits > 0) {
    result += integerStr.substring(0, extraDigits);
    digitIndex = extraDigits;
  }

  // Then walk the template
  for (const part of parsed.integerParts) {
    if (part.type === 'digit') {
      result += integerStr[digitIndex] ?? '0';
      digitIndex++;
    } else {
      result += part.char;
    }
  }

  // Prepend negative sign if needed
  if (isNegative) {
    result = '-' + result;
  }

  return result + decimalStr;
}

/**
 * Convenience wrapper that converts from meters to the display unit
 * and then applies the label format template.
 *
 * @param valueInMeters The chainage value in meters.
 * @param format The label format template.
 * @param lengthUnit The display length unit.
 * @param lengthDecimals Optional decimal-place override.
 */
/**
 * Returns a {@link LengthUnit} that `convertLength` can handle.
 * Compound `ft-in` is not a valid `LengthUnit`; this maps it to `'ft'`.
 */
export function getChainageUnit(lengthUnit: LengthUnit): LengthUnit {
  return (lengthUnit as unknown) === FeetInchUnit ? 'ft' : lengthUnit;
}

export function formatChainageLabel(
  valueInMeters: number,
  format: LabelFormat,
  lengthUnit: LengthUnit,
  lengthDecimals?: number
): string {
  const displayValue =
    convertLength(valueInMeters, 'm', getChainageUnit(lengthUnit)) ??
    valueInMeters;
  return formatChainageDisplayValue(displayValue, format, lengthDecimals);
}

/**
 * Extracts the raw numeric value from a billboard text string produced by the
 * Alignment API.  The unit suffix (e.g. `" m"`, `"ft"`) is stripped but
 * **no unit conversion is performed** — the returned number is in whatever unit
 * the API used when it generated the label.
 *
 * @returns The numeric chainage value, or `NaN` if the text cannot be parsed.
 */
export function parseChainageValue(text: string): number {
  if (!text) {
    return NaN;
  }
  // Match a number (with optional decimal and negative sign) at the start,
  // potentially followed by whitespace and a unit suffix that we discard.
  const match = /(-?\d+\.?\d*)/.exec(text);
  return match ? parseFloat(match[1]) : NaN;
}

/**
 * Returns the label symbol character from the given format template,
 * or undefined if the format does not contain a symbol.
 */
export function getSymbolFromFormat(
  format: LabelFormat
): LabelSymbol | undefined {
  if (format.includes('+')) return '+';
  if (format.includes('/')) return '/';
  return undefined;
}
